import { RoundedBox } from '@react-three/drei'
import { Suspense } from 'react'
import { Shape } from 'three'
import { useVerandaSelector } from '../../../..'
import { Helper } from '../../../../classes/helper'
import { useMarquee } from '../../../../hooks/state/useMarquee'
import { useMeasures } from '../../../../hooks/state/useMeasures'
import { useRoof } from '../../../../hooks/state/useRoof'
import { CachedMeshPhysicalMaterial } from '../../../material/cachedMeshPhysicalMaterial'
interface Props {
  name: string
}

export const MarqueeIntegrate = ({ name = '' }: Props): JSX.Element => {
  const {
    attachment,
    verandaTyp,
    userWidth,
    offsetRoofLog,
    polesTopNumber,
    roofLogWidth,
    isQubus,
    attachedSize,
  } = useMeasures()
  const { color } = useRoof()
  const { marqueeFabricColor } = useMarquee()

  const shape = new Shape()
  const shapePoles = new Shape()
  const shapeHolder = new Shape()

  const polesDimension = {
    x: verandaTyp.roofBar.depth - 0.02,
    y: 0.09,
  }

  const attachedDimension = {
    x: 0.1,
    y: 0.04,
  }
  shape
    .moveTo(0, 0)
    .lineTo(attachedDimension.x, 0)
    .lineTo(attachedDimension.x, attachedDimension.y * 0.1)
    .lineTo(attachedDimension.x * 0.8, attachedDimension.y * 0.1)
    .lineTo(attachedDimension.x * 0.8, attachedDimension.y * 0.2)
    .lineTo(attachedDimension.x * 0.2, attachedDimension.y * 0.7)
    .lineTo(attachedDimension.x * 0.2, attachedDimension.y * 0.8)
    .lineTo(0, attachedDimension.y * 0.8)

  const extrudeSettings = {
    curveSegments: 1,
    steps: 1,
    depth: userWidth,
    bevelEnabled: false,
  }

  shapePoles
    .moveTo(0, 0)
    .lineTo(polesDimension.x, 0)
    .lineTo(polesDimension.x, polesDimension.y - 0.07)
    .lineTo(polesDimension.x - 0.01, polesDimension.y - 0.07)
    .lineTo(polesDimension.x - 0.01, polesDimension.y - 0.01)
    .lineTo(polesDimension.x, polesDimension.y - 0.01)
    .lineTo(polesDimension.x, polesDimension.y)
    .lineTo(0, polesDimension.y)
    .lineTo(0, polesDimension.y - 0.01)
    .lineTo(0.01, polesDimension.y - 0.01)
    .lineTo(0.01, polesDimension.y - 0.07)
    .lineTo(0, polesDimension.y - 0.07)

  const extrudePolesSettings = {
    curveSegments: 1,
    steps: 1,
    depth: roofLogWidth,
    bevelEnabled: false,
  }

  shapeHolder
    .moveTo(polesDimension.x + 0.001, polesDimension.y - 0.01)
    .lineTo(polesDimension.x + 0.001, polesDimension.y)
    .lineTo(-0.001, polesDimension.y)
    .lineTo(-0.001, polesDimension.y - 0.01)
    .lineTo(0.01, polesDimension.y - 0.01)

  const extrudeHolderSettings = {
    curveSegments: 1,
    steps: 1,
    depth: 0.05,
    bevelEnabled: false,
  }

  const shapeCap = new Shape()
  shapeCap
    .moveTo(0.015, -0.05)
    .lineTo(0.015, polesDimension.y + 0.02)
    .lineTo(-attachedSize.z, polesDimension.y)
    .lineTo(-attachedSize.z, -0.01)
    .lineTo(0.015, -0.05)

  const extrudeCapSettings = {
    curveSegments: 1,
    steps: 1,
    depth: verandaTyp.roofBar.depth,
    bevelEnabled: false,
  }
  const shadingMode = useVerandaSelector((state) => state.scene.shadingMode)
  const fabricLength = 1
  const inAndOut = shadingMode ? roofLogWidth - 0.01 : fabricLength
  const field =
    (userWidth - (isQubus ? verandaTyp.log.width : 0)) / (polesTopNumber - 1)

  return (
    <Suspense fallback={null}>
      <group
        castShadow
        position={[-userWidth / 2, +offsetRoofLog.c, attachment.free ? 0 : 0]}
      >
        <mesh
          position={[
            userWidth,
            polesDimension.y + (attachedDimension.y * 0.7) / 2,
            -0.11,
          ]}
          rotation={[0, Helper.convertAngleToRadiant(-90), 0]}
          castShadow
          key={`marqueeBox`}
        >
          <extrudeGeometry attach="geometry" args={[shape, extrudeSettings]} />

          <CachedMeshPhysicalMaterial
            color={color}
            opacity={0.99}
            roughness={1}
            metalness={0}
            envMapIntensity={0.2}
          />
        </mesh>

        <mesh
          rotation={[0, 0, 0]}
          position={[
            userWidth / 2,
            polesDimension.y + 0.015,
            inAndOut / 2 - 0.02,
          ]}
          castShadow={true}
        >
          <boxGeometry args={[userWidth - 0.02, 0.005, inAndOut]} />

          <CachedMeshPhysicalMaterial
            color={marqueeFabricColor}
            transparent
            opacity={0.985}
            roughness={0.8}
            metalness={0.2}
          />
        </mesh>

        <mesh
          rotation={[0, 0, 0]}
          position={[userWidth / 2, polesDimension.y + 0.009, inAndOut]}
          castShadow={true}
        >
          <RoundedBox
            args={[userWidth, 0.015, 0.05]}
            radius={0.0025}
            matrixWorldAutoUpdate={undefined}
            getObjectsByProperty={undefined}
          >
            <CachedMeshPhysicalMaterial color={color} />
          </RoundedBox>
        </mesh>
        {[...Array(polesTopNumber)].map((_, index) => {
          return (
            <mesh
              position={[
                field * index -
                  (!index
                    ? -polesDimension.x / 2
                    : index === polesTopNumber - 1
                    ? polesDimension.x / 2 + verandaTyp.roofBar.depth / 2
                    : polesDimension.x / 2),
                0.002,
                inAndOut - 0.025,
              ]}
              castShadow={true}
              key={'holder' + index}
            >
              <extrudeGeometry
                attach="geometry"
                args={[shapeHolder, extrudeHolderSettings]}
              />

              <CachedMeshPhysicalMaterial color={'silver'} />
            </mesh>
          )
        })}
        {[...Array(polesTopNumber)].map((_, index) => {
          return (
            <mesh
              key={'poleTop' + index}
              position={[
                field * index -
                  (!index
                    ? -polesDimension.x / 2
                    : index === polesTopNumber - 1
                    ? polesDimension.x / 2 + verandaTyp.roofBar.depth / 2
                    : polesDimension.x / 2),
                0,
                0,
              ]}
              castShadow={true}
            >
              <extrudeGeometry
                attach="geometry"
                args={[shapePoles, extrudePolesSettings]}
              />

              <CachedMeshPhysicalMaterial color={color} />

              <RoundedBox
                args={[polesDimension.x, polesDimension.y - 0.08, 0.02]}
                radius={
                  !index
                    ? 0.0075
                    : index === polesTopNumber - 1
                    ? 0.0075
                    : 0.0025
                }
                matrixWorldAutoUpdate={undefined}
                getObjectsByProperty={undefined}
                getVertexPosition={undefined}
                position={[
                  polesDimension.x / 2,
                  polesDimension.y - 0.004,
                  roofLogWidth + 0.009,
                ]}
                rotation={[
                  index
                    ? index === polesTopNumber - 1
                      ? Helper.convertAngleToRadiant(-90)
                      : 0
                    : Helper.convertAngleToRadiant(-90),
                  0,
                  0,
                ]}
              >
                <CachedMeshPhysicalMaterial color={color} />
              </RoundedBox>
              {(index === polesTopNumber - 1 || !index) && (
                <>
                  <RoundedBox
                    args={[0.002, 0.002, roofLogWidth - inAndOut + 0.01]}
                    radius={0.0001}
                    matrixWorldAutoUpdate={undefined}
                    getObjectsByProperty={undefined}
                    getVertexPosition={undefined}
                    position={[
                      polesDimension.x / 2,
                      polesDimension.y + 0.005,
                      inAndOut + (roofLogWidth - inAndOut + 0.01) / 2,
                    ]}
                    rotation={[0, 0, 0]}
                  >
                    <CachedMeshPhysicalMaterial color={'silver'} />
                  </RoundedBox>
                </>
              )}
            </mesh>
          )
        })}
        {[...Array(2)].map((_, index) => {
          return (
            <mesh
              rotation={[0, Helper.convertAngleToRadiant(90), 0]}
              key={'CapSideMarqueeDefault' + index}
              position={[
                index ? -0.003 : userWidth - verandaTyp.roofBar.depth + 0.0025,
                0.01,
                -attachedSize.z - (attachment.free ? 0 : 0.01),
              ]}
              castShadow={true}
              receiveShadow
            >
              <extrudeGeometry
                attach="geometry"
                args={[shapeCap, extrudeCapSettings]}
              />
              <CachedMeshPhysicalMaterial color={color} />
            </mesh>
          )
        })}
      </group>
    </Suspense>
  )
}
